<template>
  <v-navigation-drawer
    :value="value"
    app
    right
    temporary
    width="760"
    @input="$emit('input', $event)"
  >
    <v-toolbar flat dense>
      <v-toolbar-title class="text-subtitle-1 font-weight-bold">
        {{ $t('classlink.runs.title') }}
      </v-toolbar-title>
      <v-spacer />
      <v-btn icon @click="$emit('input', false)">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <div class="px-4 pb-4">
      <p class="text-caption grey--text">
        {{ $t('classlink.runs.subtitle', { limit: pageSize, name: tenantName }) }}
      </p>

      <v-progress-linear v-if="loadingRuns" indeterminate color="deep-purple" />

      <v-data-table
        :headers="headers"
        :items="runs"
        :loading="loadingRuns"
        :items-per-page="pageSize"
        :no-data-text="$t('classlink.runs.empty')"
        :expanded.sync="expanded"
        item-key="id"
        show-expand
        hide-default-footer
        dense
        class="elevation-0"
      >
        <template v-slot:[`item.startedAt`]="{ item }">
          <div>{{ startedAtLocal(item) }}</div>
          <div class="text-caption grey--text">{{ relative(item.startedAt) }}</div>
        </template>

        <template v-slot:[`item.mode`]="{ item }">
          {{ $t(`classlink.runs.mode.${item.mode}`) }}
        </template>

        <template v-slot:[`item.trigger`]="{ item }">
          <div>{{ $t(`classlink.runs.trigger.${item.trigger}`) }}</div>
          <div v-if="item.triggeredBy" class="text-caption grey--text">
            {{ $t('classlink.runs.triggered_by', { email: item.triggeredBy }) }}
          </div>
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <v-chip :color="statusChipColor(item)" x-small dark>
            {{ statusChipText(item) }}
          </v-chip>
        </template>

        <template v-slot:[`item.duration`]="{ item }">
          {{ duration(item) }}
        </template>

        <template v-slot:[`item.summary`]="{ item }">
          {{ countsText(item) }}
        </template>

        <template v-slot:expanded-item="{ headers: cols, item }">
          <td :colspan="cols.length" class="py-3">
            <v-alert v-if="item.errorMessage" type="error" dense text class="mb-3">
              <div class="font-weight-bold">{{ $t('classlink.runs.error_message_title') }}</div>
              <div>{{ item.errorMessage }}</div>
            </v-alert>

            <div class="text-subtitle-2 mb-1">{{ $t('classlink.runs.detail_title') }}</div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>{{ $t('classlink.runs.col_stage') }}</th>
                  <th>{{ $t('classlink.runs.col_outcome') }}</th>
                  <th class="text-end">{{ $t('classlink.runs.col_count') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rowsOf(item)" :key="`${item.id}-${row.stage}-${row.outcome}`">
                  <td>{{ stageLabel(row.stage) }}</td>
                  <td>{{ outcomeLabel(row.outcome) }}</td>
                  <td class="text-end">{{ row.count }}</td>
                </tr>
              </tbody>
            </v-simple-table>

            <div v-if="sourceTotals(item)" class="mt-3">
              <div class="text-subtitle-2">{{ $t('classlink.runs.source_totals_title') }}</div>
              <div class="text-caption">{{ sourceTotals(item) }}</div>
            </div>

            <div v-if="item.errorSample && item.errorSample.length" class="mt-4">
              <div class="d-flex align-center">
                <span class="text-subtitle-2">{{ $t('classlink.runs.errors_title') }}</span>
                <v-spacer />
                <v-btn text x-small color="deep-purple" @click="copyErrors(item)">
                  <v-icon left x-small>mdi-content-copy</v-icon>
                  {{ $t('classlink.runs.copy') }}
                </v-btn>
              </div>
              <ul class="text-caption pl-4 mb-0">
                <li v-for="(sample, index) in item.errorSample" :key="`${item.id}-err-${index}`">
                  <code>{{ sample.sourcedId }}</code>
                  <span class="grey--text"> · {{ stageLabel(sample.stage) }}</span>
                  <span class="grey--text"> · {{ outcomeLabel(sample.outcome) }}</span>
                  <span v-if="detailText(sample)"> — {{ detailText(sample) }}</span>
                </li>
              </ul>
            </div>

            <div v-if="unresolvedOf(item).length" class="mt-4">
              <div class="text-subtitle-2">
                {{ $t('classlink.runs.unresolved_title', { total: unresolvedTotal(item) }) }}
              </div>
              <ul class="text-caption pl-4 mb-0">
                <li v-for="group in unresolvedOf(item)" :key="`${item.id}-unres-${group.reason}`">
                  {{ $t('classlink.runs.unresolved_group', { count: group.count, reason: reasonText(group) }) }}
                </li>
              </ul>
            </div>
          </td>
        </template>
      </v-data-table>
    </div>
  </v-navigation-drawer>
</template>

<script>
import rosterTenantsService from '@/services/roster-tenants.service';
import {
  RUNS_PAGE_SIZE,
  apiErrorMessage,
  detailMessage,
  formatCount,
  formatDetail,
  formatErrorSample,
  formatLocalDateTime,
  outcomeLabelKey,
  relativeTime,
  runChipColor,
  runCountsMessage,
  runDurationSeconds,
  runOutcome,
  skippedTotal,
  stageLabelKey,
  stageRows,
  unresolvedGroups,
} from '@/utils/classlinkTenants';
import snackbarMixin from '@/mixins/snackbar';

export default {
  name: 'ClassLinkSyncRunsDrawer',
  mixins: [snackbarMixin],
  props: {
    value: { type: Boolean, default: false },
    tenant: { type: Object, default: null },
  },
  data: () => ({
    runs: [],
    expanded: [],
    loadingRuns: false,
    pageSize: RUNS_PAGE_SIZE,
  }),
  computed: {
    tenantName() {
      return this.tenant ? this.tenant.displayName : '';
    },
    headers() {
      return [
        { text: this.$t('classlink.runs.col_started_at'), value: 'startedAt' },
        { text: this.$t('classlink.runs.col_mode'), value: 'mode' },
        { text: this.$t('classlink.runs.col_trigger'), value: 'trigger' },
        { text: this.$t('classlink.runs.col_status'), value: 'status' },
        { text: this.$t('classlink.runs.col_duration'), value: 'duration', sortable: false },
        { text: this.$t('classlink.runs.col_summary'), value: 'summary', sortable: false },
        { text: '', value: 'data-table-expand' },
      ];
    },
  },
  watch: {
    value(open) {
      if (open) this.loadRuns();
    },
  },
  methods: {
    async loadRuns() {
      if (!this.tenant) return;
      // Se captura el id pedido para poder descartar una respuesta fuera de
      // orden: si el drawer se reabre para otra conexión antes de que esta
      // request resuelva, `this.tenant` ya cambió y la respuesta vieja no
      // debe pisar las corridas de la conexión nueva.
      const requestedId = this.tenant.id;
      this.loadingRuns = true;
      this.runs = [];
      this.expanded = [];
      try {
        const runs = await rosterTenantsService.listRuns(requestedId, RUNS_PAGE_SIZE);
        if (this.tenant && this.tenant.id === requestedId) {
          this.runs = runs;
        }
      } catch (error) {
        if (this.tenant && this.tenant.id === requestedId) {
          this.runs = [];
          this.snackbarError(apiErrorMessage(error, this.$t('classlink.runs.load_error')));
        }
      } finally {
        if (this.tenant && this.tenant.id === requestedId) {
          this.loadingRuns = false;
        }
      }
    },
    // Hora local de inicio de la corrida (a diferencia del filtro global
    // `date`, que formatea en UTC a propósito — ver el comentario de
    // `formatLocalDateTime` en `@/utils/classlinkTenants`).
    startedAtLocal(run) {
      const locale = this.$i18n.locale === 'en' ? 'en' : 'es';
      return formatLocalDateTime(run.startedAt, locale);
    },
    relative(iso) {
      const message = relativeTime(iso);
      return message ? this.$t(message.key, message.params) : '';
    },
    // Mismo formateo que `unresolvedTotal` (y que la vista): todos los números
    // de una misma corrida agrupados con separador de miles, para que la
    // columna "Resumen" y el bloque expandido de acá abajo no puedan mostrar
    // el mismo número con y sin formato.
    countsText(run) {
      const message = runCountsMessage(run);
      if (!message) return '';
      const {
        users, courses, skipped, ambiguous, errors,
      } = message.params;
      const { locale } = this.$i18n;
      return this.$t(message.key, {
        users: formatCount(users, locale),
        courses: formatCount(courses, locale),
        skipped: formatCount(skipped, locale),
        ambiguous: formatCount(ambiguous, locale),
        errors: formatCount(errors, locale),
      });
    },
    duration(run) {
      const seconds = runDurationSeconds(run);
      return seconds === null
        ? this.$t('classlink.runs.in_progress')
        : this.$t('classlink.runs.duration_seconds', { n: seconds });
    },
    rowsOf(run) {
      return stageRows(run.summary);
    },
    // `sourceTotals` sólo lo escribe el backend en corridas `full`; la fila
    // aparece únicamente cuando esta corrida lo trae.
    sourceTotals(run) {
      const totals = run.summary ? run.summary.sourceTotals : undefined;
      if (!totals) {
        return '';
      }
      return this.$t('classlink.runs.source_totals', {
        orgs: totals.orgs,
        users: totals.users,
        classes: totals.classes,
        enrollments: totals.enrollments,
      });
    },
    // Las etapas y outcomes vienen del backend como claves libres dentro de
    // `summary.byStage`. Se traducen las conocidas y se muestra la clave cruda
    // si aparece una nueva, en vez de romper la tabla.
    stageLabel(stage) {
      const key = stageLabelKey(stage);
      return this.$te(key) ? this.$t(key) : stage;
    },
    outcomeLabel(outcome) {
      const key = outcomeLabelKey(outcome);
      return this.$te(key) ? this.$t(key) : outcome;
    },
    async copyErrors(run) {
      const text = formatErrorSample(run.errorSample);
      try {
        await navigator.clipboard.writeText(text);
        this.snackbarSuccess(this.$t('classlink.runs.copied'));
      } catch (error) {
        this.snackbarError(this.$t('classlink.runs.copy_failed'));
      }
    },
    statusChipColor(run) {
      return runChipColor(run);
    },
    statusChipText(run) {
      const outcome = runOutcome(run);
      if (outcome) {
        const outcomeKey = `classlink.outcome.${outcome}`;
        return this.$te(outcomeKey) ? this.$t(outcomeKey) : outcome;
      }
      return this.$t(`classlink.status.${run.status}`);
    },
    // Mismo patrón `$te ? $t : fallback` que `stageLabel`/`outcomeLabel`: si el
    // backend manda un motivo sin clave de i18n todavía, se ve el detalle
    // crudo (vía `formatDetail`) y no la clave sin traducir en pantalla.
    detailText(sample) {
      const message = detailMessage(sample.detail);
      if (!message) return '';
      return this.$te(message.key) ? this.$t(message.key, message.params) : formatDetail(sample.detail);
    },
    unresolvedOf(run) {
      return unresolvedGroups(run);
    },
    // Total real de omitidos (no la muestra): mismo número que ve la fila en
    // `countsText`, para que la fila y el drawer nunca puedan mostrar cifras
    // distintas.
    unresolvedTotal(run) {
      return formatCount(skippedTotal(run.summary), this.$i18n.locale);
    },
    // Copy corto sin params: `group` junta la muestra de varios registros, así
    // que no correspondía mostrar los params de uno solo (`detailMessage` con
    // `group.sample[0]`) como si fueran de todo el grupo. Mismo patrón
    // `$te ? $t : fallback` que el resto del archivo.
    reasonText(group) {
      const key = `classlink.reasons_grouped.${group.reason}`;
      return this.$te(key) ? this.$t(key) : group.reason;
    },
  },
};
</script>
